import {
    type AssistantType,
    type ClassAccess,
    type ClassOptionsOverrides,
    type DocumentType,
    type GradebookType,
    type HostType,
    type OrganizationType,
    type ProctoringType,
    type RosterType,
    type SchoolLevelOptions,
    type StudentType,
    type ZoomAppId
} from './adminConfig';
import { type ConfigIngestModes, type Permission } from './recordingTypes';
import type { DraftMeetingData } from '../../../models/types';
import type {
    ClassRecord,
    ClassSessionGroup,
    ClassSessionInfo,
    ClassStudentGroup,
    ClassStudentGroupSet
} from '../context/types';
import { type UserRole } from '../views/AdminSettings/constants/user-roles';

export enum ApiKeyScope {
    ATTENDANCE_READ = 'attendance:read',
    CLASS_READ = 'class:read',
    CLASS_WRITE = 'class:write',
    ENROLLMENT_READ = 'enrollment:read',
    ENROLLMENT_WRITE = 'enrollment:write',
    SCHEDULE_READ = 'schedule:read',
    SCHEDULE_WRITE = 'schedule:write',
    TEMPLATE_READ = 'template:read',
    USER_READ = 'user:read',
    USER_WRITE = 'user:write'
}

export enum Status {
    NotStarted = 'NOT_STARTED',
    InProgress = 'IN_PROGRESS',
    Complete = 'COMPLETE'
}

export interface Pagination {
    total_records: number;
    current_page: number;
    total_pages: number;
    next_page: number | null;
    prev_page: number | null;
}

export interface ApiKey {
    id: number;
    name: string;
    description: string;
    scopes: ApiKeyScope[];
    is_active: boolean;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface ApiKeyCreate {
    name: string;
    description: string;
    is_active: boolean;
    expires_at: string | null;
    scopes: ApiKeyScope[];
}

export interface ApiKeyEdit {
    scopes: ApiKeyScope[];
    is_active: boolean;
}

export interface CreatedApiKeyResponse {
    rawValue: string;
    key: ApiKey;
}

export interface SchoolSettings {
    organization_type: OrganizationType;
    host_title: HostType;
    assistants_title: AssistantType;
    students_title: StudentType;
    roster_title: RosterType;
    gradebook_title: GradebookType;
    document_title: DocumentType;
    proctoring_title: ProctoringType;
    vanity_url: string;
    domains: string;
    allow_pmi: boolean;
    forced_authentication: boolean;
    has_document: boolean;
    has_gradebook: boolean;
    has_assessments: boolean;
    has_lms: boolean;
    has_whiteboard: boolean;
    has_assignments: boolean;
    enable_log_auto_upload: boolean;
    has_panic_mode: boolean;
    has_surveys: boolean;
    has_polling: boolean;
    has_proctoring: boolean;
    has_share_files: boolean;
    has_chat: boolean;
    has_lose_focus_tracking: boolean;
    has_web_app: boolean;
    force_web_app_chromebooks: boolean;
    has_app: boolean;
    has_zoom: boolean;
    zoom_app_id: ZoomAppId;
    join_class_by_meeting_id: boolean;
    auto_transcriptions: boolean;
    class_notes: boolean;
    send_invite_emails: boolean;
    show_meeting_info: boolean;
    recording_auto_save: boolean;
    recording_local_save: boolean;
    recording_cloud_save: boolean;
    recording_interactive_player: boolean;
    recording_initial_permission: Permission;
    recording_ingest_mode: ConfigIngestModes;
    recording_participants_can_view: boolean;
    recording_participants_can_download: boolean;
    recording_restrict_gallery: boolean;
    recording_tab_access: ClassAccess;
    attendance_tab_access: ClassAccess;
    restrict_sessions_recordings_by_groups: boolean;
    prompt_teachers_for_feedback: boolean;
    prompt_students_for_feedback: boolean;
    notify_outdated_class_version: boolean;
    enforce_class_minimum_version: boolean;
    auto_verify: 'never' | 'optional' | 'always';
    has_google: boolean;
    has_ms_office: boolean;
    has_ai_ta: boolean;
    class_access: ClassAccess;
    allow_instructor_swap: boolean;
    enable_breakout_room_cloud_recording: boolean;
    prevent_leave_session_redirect: boolean;
    enable_breakout_room_security: boolean;
    whiteboard_screenshot_feature: boolean;
    instructor_chat_download: boolean;
    is_bb_data: boolean;
    data_portal_admin: boolean;
    data_portal_business_analyst: boolean;
    data_portal_class_business_analyst: boolean;
    data_portal_customer_support: boolean;
    data_portal_instructor: boolean;
    data_portal_student: boolean;
    allow_ai_for_support: boolean;
    allow_ai_for_insights: boolean;
    azure_tenant_id: string | null;
    azure_client_id?: string;
    has_azure_phone_numbers: boolean;
    options: SchoolLevelOptions;
    overrides: ClassOptionsOverrides;
}

export interface SchoolProperties {
    zoom_app_id: ZoomAppId;
}

export interface GetAdminSettings {
    data: SchoolSettings & SchoolProperties;
    prefix: string;
}

export interface GetUser {
    zoom_login_id: string;
    data?: {
        email?: string;
    };
}

export interface AuthMeResponse {
    user_id: string;
    email: string;
    user_name: string;
    roles: UserRole[];
}

export interface PaginatedUsers {
    data: UsersRecord[];
    pagination: Pagination;
}

export interface UsersRecord {
    user_id: number;
    email?: string;
    user_name: string;
    ext_user_id?: string;
    roles: UserRole[];
}

export interface SyncTimeItem {
    hours: number;
    minutes: number;
    ampm: 'AM' | 'PM';
}

export interface SyncTimeRecord {
    [index: string]: SyncTimeItem;
}

export enum SyncType {
    AllClasses = 'all-classes',
    SomeClasses = 'some-classes'
}

export enum ContentLearningTools {
    LmsContent = 'lms-content',
    InstructorChoice = 'instructor-choice'
}

export interface LmsSyncSettings {
    sync_enabled?: boolean;
    enable_roster?: boolean;
    enable_roster_type?: string; // todo: renamed to roster_type. Delete after beta
    roster_type?: string;
    enable_attendance?: boolean;
    enable_attendance_grade?: boolean; // create line item in LMS gradebook for overall attendance like in role call.
    attendance_grade_tardy_score?: number;
    enable_content?: boolean;
    content_learning_tools?: ContentLearningTools;
    enable_content_class_ids?: string; // todo: renamed to sync_course_ids. Delete after beta
    sync_course_ids?: string;
    enable_content_type?: string; // todo: renamed to content_type. Delete after beta
    sync_type?: SyncType;
    sync_count?: number;
    sync_times?: SyncTimeRecord;
    enable_sessions?: boolean;
    enable_content_picker?: boolean;
    has_api_token?: boolean;
}

export interface LTICredential {
    lti_creds_uuid: string;
    zoom_login_id: string;
    title: string;
    type: string;
    client_id: string;
    shared_secret: string;
    school_uuid: string;
    active: boolean;
    email_attribute_name: string;
    course_id_attribute_name: string;
    course_id_prefix: string;
    person_id_attribute_name: string;
    person_id_prefix: string;
    lti_version: string;
    consumer_client_id: string;
    learn_app_key: string;
    learn_app_secret: string;
    consumer_authorization_url: string;
    consumer_access_token_url: string;
    consumer_authorization_config_url: string;
    rsa_public_key: string;
    debug_mode: boolean;
    updated_at: Date;
    created_at: Date;
    lms_base_url?: string;
    lms_key?: string;
    lms_secret?: string;
    sync_settings?: LmsSyncSettings;
    last_sync_timestamp?: number;
    grant_instructor_system_role: boolean;
    admin_username?: string;
}

export interface UsersRecordExtended extends UsersRecord {
    pendingChanges?: boolean;
}

export interface UserRecord {
    id: number;
    email: string;
    user_name: string;
}

export interface PostUpdateZoomUserResult {
    success: boolean;
    results: number;
}

export interface SchoolInfo {
    logo: string;
    name: string;
    prefix: string;
    school_uuid: string;
    zoom_app_id: ZoomAppId;
}

export interface CreateUserRequestInput {
    email?: string;
    ext_user_id?: string;
    name: string;
    roles: UserRole[];
    password?: string;
}

export interface CreateUsersRequestInput {
    user_uuid: string; // auth
    users: CreateUserRequestInput[];
}

export interface UpdateUserRequestInput {
    user_uuid: string; // auth
    user_id: number;
    email?: string;
    ext_user_id?: string;
    name?: string;
    roles?: UserRole[];
    password?: string;
}

// copied from node_pod/models/types.ts
export interface ZoomDialInNumber {
    type: string;
    number: string;
    country: string;
    country_name: string;
}

// copied from node_pod/models/types.ts
export interface ZoomMeetingSettings {
    allow_multiple_devices: boolean;
    alternative_hosts: string;
    approval_type: number;
    approved_or_denied_countries_or_regions: { enable: boolean };
    audio: string;
    auto_recording: string;
    close_registration: boolean;
    cn_meeting: boolean;
    encryption_type: string;
    enforce_login_domains: string;
    enforce_login: boolean;
    global_dial_in_countries: string[];
    global_dial_in_numbers: ZoomDialInNumber[];
    host_video: boolean;
    in_meeting: boolean;
    jbh_time: number;
    join_before_host: true;
    meeting_authentication: boolean;
    mute_upon_entry: true;
    participant_video: boolean;
    registrants_confirmation_email: true;
    registrants_email_notification: boolean;
    request_permission_to_unmute_participants: boolean;
    show_share_button: boolean;
    use_pmi: boolean;
    waiting_room: boolean;
    watermark: boolean;
}

// copied from node_pod/models/types.ts
export enum MeetingProvider {
    Zoom = 'Zoom',
    ACS = 'ACS',
    Teams = 'Teams'
}

// copied from node_pod/models/types.ts
export interface ZoomMeetingData {
    meeting_provider: MeetingProvider.Zoom;

    // Data from the Zoom REST API
    id: number;
    type: number;
    uuid: string;
    topic: string;
    agenda: string;
    status: string;
    host_id: string;
    duration: number;
    join_url: string;
    password: string;
    timezone: string;
    start_url: string;
    created_at: string;
    start_time: string;
    host_email: string;
    h323_password: string;
    pstn_password: string;
    encrypted_password: string;
    settings: Partial<ZoomMeetingSettings>;

    // The following are not part of the Zoom REST API
    external_meeting_id: string;
}

// copied from node_pod/models/types.ts
export interface AcsMeetingData {
    meeting_provider: MeetingProvider.ACS;

    id: number;
    topic: string;
    password: string;
    join_url: string;
    external_meeting_id: string;
    settings: {
        join_before_host: boolean;
        waiting_room: boolean;
    };
}

export interface LeadInstructor {
    student_id: string;
    student_uuid: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    ext_user_id?: string;
}

export type ClassData = {
    class: ClassRecord;
    classGroups: ClassStudentGroup[];
    classGroupSets: ClassStudentGroupSet[];
    classSessionGroups: ClassSessionGroup[];
    classSessions: ClassSessionInfo[];
    meeting: ZoomMeetingData | AcsMeetingData | DraftMeetingData;
    school: SchoolInfo;
    userGroups: ClassStudentGroup[];
    lead_instructor?: LeadInstructor;
};

export type ClassInfo = ClassRecord & {
    instructor?: Omit<LeadInstructor, 'email'>;
    student_groups?: any[];
    next_session?: {
        class_session_uuid: string;
        start_time: string;
        end_time: string;
        is_live: boolean;
        timezone: string;
    };
};
export interface ClassUserSessionData {
    class_session_uuid: string;
    start_time: string;
    end_time: string;
    timezone: string;
    class_session_topic?: string;
    notes: any[];
    chats: any[];
}
